import * as React from 'react';
import clone from 'clone';
import { ArticleList, ArticleType } from '../../../../amorg-common';
import { articlesClientApi } from '../../../../api/articlesClientApi';
import { Flex, FlexItem } from '../../../../designSystem/compositeComponents/FlexHelpers';
import { SelectField } from '../../../../designSystem/formField/SelectField';
import { AnimatedCollapsibleContent } from '../../../../designSystem/collapsible/AnimatedCollapsibleContent';
import { Button } from '../../../../designSystem/button/Button';
import { ButtonRow } from '../../../../designSystem/button/ButtonRow';
import { ListRowWithChildren, ListRow } from '../../../../designSystem/compositeComponents/ListRow';
import { Modal } from '../../../../designSystem/Modal';
import { colors } from '../../../../designSystem/colors/color';
import { TextInput } from '../../../../designSystem/inputs/TextInput';
import { FormField } from '../../../../designSystem/formField/FormField';


export type ArticleFilter = {
    typeId?:string;
    listId?:string;
    topicId?:string;
    searchTerm?:string;
    released?:boolean;
}

type ArticleFilterPanelProps = {
    filter:ArticleFilter;
    buttons?:React.ReactNode[];
    onFilterChange:(newFilter:ArticleFilter) => void;
}

export function ArticleFilterPanel(props:ArticleFilterPanelProps):JSX.Element
{
    // const [showClassicFilter, setShowClassicFilter] = React.useState(true);
    const [lists, setLists] = React.useState<ArticleList[]>([]);
    const [types, setTypes] = React.useState<ArticleType[]>([]);
    const [searchTerm, setSearchTerm] = React.useState('');

    React.useEffect(() => {
        const getData = async () => {
            setLists(await articlesClientApi.getLists())
            setTypes(await articlesClientApi.getTypes());
        };
        getData();
        setSearchTerm(props.filter.searchTerm || '');
    },[]);

    async function handleFilterChange(newParams:ArticleFilter)
    {
        props.onFilterChange(newParams);
    }

    async function handleListFilterChange(listId:string)
    {
        const newFilter = clone(props.filter);
        if(listId === '')
        {
            newFilter.listId = undefined;
            newFilter.topicId = undefined;
        }
        else
        {
            newFilter.listId = listId;
            const list = lists.find(l => l.id === listId);
            if(!list.topics.some(t => t.id === newFilter.topicId))
                newFilter.topicId = undefined;
        }

        handleFilterChange(newFilter);
    }

    async function handleTopicFilterChange(topicId:string)
    {
        const newFilter = clone(props.filter);
        if(topicId === '')
            newFilter.topicId = undefined;
        else
        {
            newFilter.topicId = topicId;
            newFilter.listId = lists.find(l => l.topics.some(t => t.id === topicId)).id;
        }
        handleFilterChange(newFilter);
    }

    async function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>)
    {
        const value = e.currentTarget.value;
        
        if(e.key === 'Enter')
        {
            handleFilterChange({...props.filter, searchTerm:value});
        }
    }

    async function handleClearSearch()
    {
        setSearchTerm('');
        handleFilterChange({...props.filter, searchTerm:undefined});
    }

    return (
        <>
            <AnimatedCollapsibleContent open={true}>

                <Flex gap={32} justifyContent="stretch" alignItems='flex-end'>
                                
                    <FlexItem width="25%">
                        <SelectField
                            label='Liste'
                            selectedValue={props.filter.listId || ''}
                            emptyText="Alle lister"
                            options={lists.map(l => ({  text: l.name, value: l.id }))}
                            onChange={handleListFilterChange} 
                        />
                        </FlexItem>
                        
                        <FlexItem width="25%">
                        <SelectField
                            label='Emne'
                            selectedValue={props.filter.topicId || ''}
                            emptyText="Alle emner"
                            options={lists.map(l => 
                                ({text:l.name, options:l.topics.map(t => ({text:t.name, value:t.id }))}))} 
                            onChange={handleTopicFilterChange} 
                        />
                        </FlexItem>
                    
                        <FlexItem width="25%">
                        <SelectField
                            label='Type' 
                            selectedValue={props.filter.typeId || ''}
                            emptyText="Alle typer"
                            options={types.map(t => ({text:t.name, value:t.id }))}
                            onChange={(value) => handleFilterChange({...props.filter, typeId:value==='' ? undefined : value})} 
                        />
                        </FlexItem>
                        <FlexItem width="25%">
                        <SelectField
                            label='Status'
                            selectedValue={props.filter.released === true 
                                ? '1' 
                                : props.filter.released === false 
                                    ? '0' 
                                    : ''
                            }
                            emptyText="Alle"
                            options={[
                                {text:'Udgivet', value:'1'},
                                {text:'Ikke udgivet', value:'0'}
                            ]}
                            onChange={(value) => handleFilterChange({...props.filter,  
                                released:value==='1' 
                                    ? true 
                                    : value==='0' 
                                        ? false 
                                        : undefined})
                            }
                        />
                        </FlexItem>
                        <Flex gap={4} alignItems='center'>
                            <FormField
                                label=''
                                inputComponent={
                                    <Flex alignItems='center' gap={4}>
                                        <TextInput
                                            leadingIcon={'interface_actions_search'}
                                            placeholder='Søgetekst...'
                                            value={searchTerm}
                                            onChange={(value) => setSearchTerm(value)}
                                            onKeyDown={handleSearchKeyDown}
                                        />
                                        <Button variant="text" icon='cancel' onClick={handleClearSearch} />
                                    </Flex>
                                }
                            />
                        </Flex>
                </Flex>

            </AnimatedCollapsibleContent>
        </>
    );
}

type FilterModalButtonProps = {
    lists:ArticleList[],
    types:ArticleType[],
    filter:ArticleFilter,
    buttons?:React.ReactNode[],
    onFilterChanged:(newFilter:ArticleFilter)=>void
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function FilterModalButton(props:FilterModalButtonProps):JSX.Element
{
    const [show, setShow] = React.useState(false);

    function handleClickList(listId:string)
    {
        const list = props.lists.find(l => l.id === listId);
        const topicId = list.topics.some(t => t.id === props.filter.topicId)
            ? props.filter.topicId
            : undefined;

        props.onFilterChanged(
            {
                ...props.filter, listId, topicId
            }
        );
        setShow(false);
    }

    function handleClickTopic(topicId:string)
    {
        const listId = props.lists.find(l => l.topics.some(t => t.id === topicId)).id;

        props.onFilterChanged(
            {
                ...props.filter, listId, topicId
            }
        );
        setShow(false);
    }

    function handleClickAll()
    {
        props.onFilterChanged(
            {
                
                ...props.filter, 
                listId:undefined, 
                topicId:undefined
            }
        );
        setShow(false);
    }

    

    return (
        <div>
            <ButtonRow>
                <Button variant="outlined" text='Rediger filter' onClick={() => setShow(true)} />
                {props.buttons}
            </ButtonRow>

            <Modal title={'Filtrér lovkrav'} showModal={show}
                onCancel={() => setShow(false)}
                content={
                    <Flex flexDirection="column" alignItems='stretch' gap={16}>
                        <Flex justifyContent="stretch" gap={32}>
                            <div style={{flex:1}}>
                            <SelectField
                                label='Type' 
                                selectedValue={props.filter?.typeId || ''}
                                emptyText="Alle typer"
                                options={props.types.map(t => ({text:t.name, value:t.id }))}
                                onChange={(value) => props.onFilterChanged({...props.filter, typeId:value=='' ? undefined : value})} 
                            />
                            </div>

                            <div style={{flex:1}}>
                            <SelectField
                                label='Status'
                                selectedValue={props.filter.released === true 
                                    ? '1' 
                                    : props.filter.released === false 
                                        ? '0' 
                                        : ''
                                }
                                emptyText="Alle"
                                options={[
                                    {text:'Udgivet', value:'1'},
                                    {text:'Ikke udgivet', value:'0'}
                                ]}
                                onChange={(value) => props.onFilterChanged({...props.filter, 
                                    released:value==='1' 
                                        ? true 
                                        : value==='0' 
                                            ? false 
                                            : undefined
                                })}
                            />
                            </div>
                        </Flex>
                        <Flex flexDirection="column" gap={8}>
                            <ButtonRow>
                                <Button variant='outlined' text={'Vælg alle lister/emner'} onClick={handleClickAll} />
                            </ButtonRow>

                            {props.lists.map(list => {
                                const listArticleCount = list.topics.reduce((acc, t) => acc + t.articleCount, 0);
                                const listHasArticles = listArticleCount > 0;
                                return (
                                    <ListRowWithChildren key={list.id} expanded={true} sizeVariant="medium" styleVariant={"neutral"}
                                        stripColor={listHasArticles ? colors.icon.active : colors.icon.passive}
                                        onClick={listHasArticles ? () => handleClickList(list.id) : undefined}
                                        content={
                                            <Flex>
                                                <span style={{flexGrow:1}}>{list.name}</span>
                                                <span>{listArticleCount} lovkrav</span>
                                            </Flex>
                                        }
                                        childRows={
                                            <> 
                                                {list.topics.map(topic => 
                                                    {
                                                        const topicHasArticles = topic.articleCount > 0;
                                                        return (
                                                            <ListRow key={topic.id} sizeVariant="medium" styleVariant="neutral" level='level2' 
                                                                stripColor={topicHasArticles ? colors.icon.active : colors.icon.passive}
                                                                onClick={topicHasArticles ? () => handleClickTopic(topic.id) : undefined}>

                                                                <Flex>
                                                                    <span style={{flexGrow:1}}>{topic.name}</span>
                                                                    <span>{topic.articleCount} lovkrav</span>
                                                                </Flex>

                                                            </ListRow>
                                                        )
                                                    }
                                                )}
                                            </>
                                        }
                                    />
                                );
                            })}
                        </Flex>
                    </Flex>
                } 
            />
        </div>
    );
}